import * as Layout from '../../../../src/components/layout'
import * as React from 'react'
import * as UI from '@brewlogix/blx-ui'
import { Brand, InputGetById, InputGetBySlug, Location } from 'src/types/schema/graphql'
import {
    Details,
    Highlights,
    LocationDescriptionHighlights,
    NearbyAttractions,
    PeopleAlsoLike,
    TourDetails,
} from 'src/features/locations/components'
import { GetServerSideProps, NextPage } from 'next'
import { useEffect, useState } from 'react'
import AmenitiesAndMore from 'src/features/locations/components/Amenities/AmenitiesAndMore'
import BrewOptions from 'src/features/locations/components/BrewOptions'
import BrewsList from 'src/features/brewsList/components/BrewsList'
import { IDynamicData } from 'src/features/seo/types'
import SEOTags from 'src/features/seo/components/SEOTags'
import ScrollTrigger from 'gsap/dist/ScrollTrigger'
import StructuredData from 'src/features/seo/components/StructuredData'
import { TabConfigItem } from 'src/components/layout/LinkTabsRow'
import createLinkTabsStore from 'src/hooks/createLinkTabsStore'
import createStickyNavStore from 'src/hooks/createStickyNavStore'
import { getLocationBySlug } from 'src/features/locations/utils/graph'
import { parseLocationMeta } from 'src/features/seo/utils/helpers'
import { useBrewsListStore } from 'src/features/brewsList/state/useBrewsListStore'
import { useLocationSlugStore } from 'src/features/locations/state/useLocationSlugStore'
import { useRouter } from 'next/router'
import { apiRequest, RequestInput } from 'src/api/apiRequest'
import getBrandByIdProducts from 'src/features/locations/utils/graph/getBrandByIdProducts'
import LocationGallery from 'src/features/locations/components/LocationGallery'
import parseEnvSubdomain from 'src/util/helpers/parseEnvSubdomain'
import { FeaturedBrewCard } from 'src/features/brewer/components'
import { IsThisYourBreweryModal } from 'src/features/locations/components/IsThisYourBreweryModal'

export const locationsTabsStore = createLinkTabsStore('locationsLinkTabs')
export const locationsStickyNavStore = createStickyNavStore('locationsStickyNav')

const Page: NextPage<{ location: Location; seoData: IDynamicData }> = ({ location, seoData }) => {
    const setSelectedElement = locationsTabsStore((state) => state.setSelectedElement)
    const setLocation = useLocationSlugStore((state) => state.setLocation)

    const setIsSticky = locationsStickyNavStore((state) => state.setIsSticky)
    const setIsTabsSticky = locationsStickyNavStore((state) => state.setIsTabsSticky)
    const isSticky = locationsStickyNavStore((state) => state.isSticky)

    const populateAvailableProducts = useBrewsListStore((state) => state.populateAvailableProducts)
    const products = useBrewsListStore((state) => state.products)
    const setProducts = useBrewsListStore((state) => state.setProducts)
    const paginatedProducts = useBrewsListStore((state) => state.paginatedProducts)
    const page = useBrewsListStore((state) => state.page)
    const styleFilter = useBrewsListStore((state) => state.styleFilter)
    const searchTerm = useBrewsListStore((state) => state.searchTerm)
    const availabilityFilter = useBrewsListStore((state) => state.availabilityFilter)

    const router = useRouter()
    const [isPainted, setPainted] = useState(true)

    const headerRef = React.useRef<HTMLDivElement>()
    const aboutRef = React.useRef<HTMLDivElement>()
    const attractionsRef = React.useRef<HTMLDivElement>()
    const brewsRef = React.useRef<HTMLDivElement>()
    const stickyHeaderRef = React.useRef<HTMLDivElement>()
    const containerRef = React.useRef<HTMLDivElement>()
    const tabsRef = React.useRef<HTMLDivElement>()
    const subdomain = parseEnvSubdomain()

    const tabsConfig: TabConfigItem[] = [
        { id: 0, text: 'About', tag: 'about', action: () => router.push('#about') },
        {
            id: 2,
            text: 'Nearby Attractions',
            tag: 'attractions',
            action: () => router.push('#attractions'),
        },
        {
            id: 3,
            text: 'Brews',
            tag: 'brews',
            extra: products.length + '',
            action: () => router.push('#brews'),
        },
        { id: 4, text: 'Amenities', tag: 'amenities', action: () => router.push('#amenities') },
    ]

    useEffect(() => {
        setLocation(location)
        setIsSticky(false)
        ;(async () => {
            await populateAvailableProducts(location?.id, location?.brand?.id)
            const input: RequestInput<InputGetById> = {
                query: getBrandByIdProducts,
                args: { input: { itemId: location?.brand?.id } },
                file: 'pages/brewer/[slug].ts',
                endpoint: 'getBrandById',
                noAlerts: true,
                publicQuery: true,
            }
            const { data } = await apiRequest<Brand, InputGetById>(input)

            if (data) {
                setProducts(data?.products)
            }
        })().catch((err) => {
            console.log('## Error getting brand: ', err)
        })
    }, [])

    React.useEffect(() => {
        if (typeof window !== 'undefined') {
            setPainted(true)
        }
        return () => {
            setPainted(false)
        }
    }, [])

    React.useEffect(() => {
        ScrollTrigger.killAll()
        ScrollTrigger.create({
            trigger: stickyHeaderRef.current,
            start: 'bottom top+=25',
            end: 'bottom bottom',
            // leave for testing
            // markers: true,
            onLeaveBack: () => {
                setIsSticky(false)
                setIsTabsSticky(false)
                setSelectedElement('about')
            },
            onLeave: () => {
                setIsSticky(true)
                setIsTabsSticky(true)
            },
        })

        ScrollTrigger.create({
            trigger: aboutRef.current,
            start: `top-=50 top+=${headerRef?.current?.offsetHeight + 50}`,
            end: `bottom-=50 top+=${headerRef?.current?.offsetHeight + 50}`,
            // leave for testing
            // markers: true,
            onEnterBack: () => setSelectedElement('about'),
            onEnter: ({ isActive }) => isActive && setSelectedElement('about'),
        })
        ScrollTrigger.create({
            trigger: attractionsRef.current,
            start: `top top+=${headerRef?.current?.offsetHeight + 100}`,
            end: `bottom top+=${headerRef?.current?.offsetHeight + 100}`,
            // leave for testing
            // markers: true,
            onEnterBack: ({ isActive }) => isActive && setSelectedElement('attractions'),
            onEnter: ({ isActive }) => isActive && setSelectedElement('attractions'),
            onLeave: ({ isActive }) => {
                if (isActive && products?.length === 0) setSelectedElement('amenities')
            },
        })

        if (products?.length) {
            ScrollTrigger.create({
                trigger: brewsRef.current,
                start: `top-=50 top+=${headerRef.current.offsetHeight + 70}`,
                end: `bottom top+=${headerRef.current.offsetHeight + 70}`,
                // leave for testing
                // markers: true,
                onEnterBack: ({ isActive }) => isActive && setSelectedElement('brews'),
                onEnter: ({ isActive }) => isActive && setSelectedElement('brews'),
                onLeave: ({ isActive }) => !isActive && setSelectedElement('amenities'),
            })
        }

        return () => {
            ScrollTrigger.killAll()
        }
    }, [paginatedProducts, page, styleFilter, searchTerm, availabilityFilter])

    useEffect(() => {
        setTimeout(() => {
            window.scrollTo({
                top: 0,
                left: 0,
            })
        }, 0)
    }, [router.pathname])

    if (!isPainted) return <UI.Loading />

    return (
        <>
            <SEOTags schemaType='location' dynamicData={seoData} />
            <StructuredData schemaType='location' dynamicData={seoData} />
            <Layout.PageLayout isSticky={isSticky}>
                <div ref={stickyHeaderRef}>
                    <Layout.BreadCrumbs
                        crumbs={[
                            {
                                breadcrumb:
                                    location.brand &&
                                    (location.brand.displayTitle || location.brand.title),
                                href: `/brewer/${(location.brand && location.brand.slug) || ''}`,
                            },
                            {
                                breadcrumb: encodeURIComponent(location.title),
                                href: `/brewer/${
                                    (location.brand && location.brand.slug) || ''
                                }/locations/${location.slug}`,
                            },
                        ]}
                    />
                </div>

                <div className='relative mx-auto flex h-full w-full flex-col justify-center'>
                    <div
                        className='sticky top-0 z-20 min-h-[6rem] w-full snap-y bg-white'
                        ref={headerRef}
                    >
                        <div className='mx-auto w-full md:px-4 lg:px-0'>
                            <Layout.Header location={location} isSticky={isSticky} />
                        </div>
                    </div>
                    {process.env.NEXT_PUBLIC_SHOW_LOCATION_IMAGES &&
                        location?.assets?.length > 0 && <LocationGallery location={location} />}
                    <div
                        ref={tabsRef}
                        className='sticky top-[5.95rem] z-20 w-full snap-y md:top-[5.45rem] lg:top-[5.95rem]'
                    >
                        <Layout.LinkTabsRow
                            tabs={tabsConfig}
                            tabStore={locationsTabsStore()}
                            stickyStore={locationsStickyNavStore()}
                            brandId={location?.brand?.id}
                        />
                    </div>
                    <div
                        className={
                            'mx-auto  flex w-full flex-col px-4 md:flex-row md:space-x-4  xl:max-w-[1440px]  xl:space-x-8 xl:px-20'
                        }
                    >
                        <div className='mt-12 md:mt-auto md:w-3/5 xl:w-2/3'>
                            <div className='content__scroll space-y-12' ref={containerRef}>
                                <div className='md:hidden'>
                                    <Details location={location} />
                                    <a
                                        href={`https://${subdomain}.marketmybrewery.com/request-account?claimBrand=${location?.brand?.id}`}
                                        target='_blank'
                                        rel='noreferrer'
                                        className='md:hidden'
                                    >
                                        <UI.Button
                                            theme='link'
                                            label='Is This Your Brewery?'
                                            icon='HeroIconsSolid.QuestionMarkCircle'
                                            iconAlignment='left'
                                            customClasses={'px-0 mt-6 lg:mt-0 flex flex-nowrap'}
                                        />
                                    </a>
                                </div>
                                <LocationDescriptionHighlights
                                    location={location}
                                    locationDescriptionRef={aboutRef}
                                />

                                <div className='mt-12 md:hidden'>
                                    <Highlights highlights={location.locationHighlights || []} />
                                </div>

                                <NearbyAttractions
                                    attractions={location.traits.nearbyAttractions || []}
                                    attractionsRef={attractionsRef}
                                />
                                {location?.traits?.tourInfo?.[0]?.toursAvailable && (
                                    <TourDetails
                                        tourDetails={location?.traits?.tourInfo[0]}
                                        timezone={location?.timezone}
                                    />
                                )}
                                {products?.length > 0 && (
                                    <BrewsList allProducts={products} brewsRef={brewsRef} />
                                )}
                            </div>
                            {location?.traits?.brewOptions?.length > 0 && (
                                <BrewOptions location={location} />
                            )}
                            <AmenitiesAndMore location={location} />
                        </div>
                        <div className='mt-8 max-w-[27.5rem] md:mt-12 md:w-1/2 xl:w-2/5'>
                            <div className='sticky top-44 space-y-12'>
                                <div className='hidden md:block'>
                                    <Details location={location} />
                                </div>
                                <div className='hidden md:block'>
                                    <Highlights highlights={location.locationHighlights || []} />
                                </div>
                                {location?.brand?.featuredBrew && (
                                    <div>
                                        <FeaturedBrewCard
                                            featuredBrew={location?.brand?.featuredBrew}
                                            locationId={location?.slug}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className='mx-auto  flex w-full flex-col px-4 md:flex-row md:space-x-4  xl:max-w-[1440px]  xl:space-x-8 xl:px-20'>
                        <PeopleAlsoLike location={location} />
                    </div>
                </div>
            </Layout.PageLayout>
            <IsThisYourBreweryModal brand={location.brand} />
        </>
    )
}

export const getServerSideProps: GetServerSideProps = async (context) => {
    const { params } = context
    let location: Location
    let seoData = null
    const input: RequestInput<InputGetBySlug> = {
        query: getLocationBySlug,
        args: {
            input: {
                parentSlug: String(params.slug),
                itemSlug: String(params?.locationSlug),
            },
        },
        file: 'pages/brewer/[slug]/locations/[locationSlug].ts',
        endpoint: 'getLocationBySlug',
        noAlerts: true,
        publicQuery: true,
    }
    const { data } = await apiRequest<Location, InputGetBySlug>(input)
    if (data) {
        location = data
        seoData = parseLocationMeta(data)
    }

    if (!data) {
        return {
            notFound: true,
        }
    }
    return {
        props: { location: location, seoData: seoData },
    }
}

export default Page
